// src/stores/notificationsStore.js
import { defineStore } from 'pinia';
import NotificationsService from '@/services/notificationsService';

const LOCAL_NOTIFICATIONS_KEY = 'local-fallback-notifications';

function readLocalFallbackNotifications() {
  try {
    const raw = localStorage.getItem(LOCAL_NOTIFICATIONS_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch (error) {
    return [];
  }
}

function sortByDateDesc(items) {
  return [...items].sort((a, b) => {
    const dateA = new Date(a.created_at || 0).getTime();
    const dateB = new Date(b.created_at || 0).getTime();
    return dateB - dateA;
  });
}

function buildNotificationSignature(notification = {}) {
  const data = notification?.data || {};
  const scopeId = data.sale_invoice_id || data.sale_id || data.customer_id || '';
  const amount = data.amount || data.wallet_credit_amount || '';
  const base = [notification?.type || '', notification?.message || '', scopeId, amount]
    .map((v) => String(v).trim().toLowerCase())
    .join('|');
  return base;
}

function resolveNotificationMessage(notification = {}) {
  const direct = typeof notification.message === 'string' ? notification.message.trim() : '';
  if (direct) {
    if (notification?.type === 'payment_received' && /^montant\s*:/i.test(direct)) {
      return `Encaissement client: ${direct}`;
    }
    return direct;
  }

  const dataMessage = typeof notification?.data?.message === 'string'
    ? notification.data.message.trim()
    : '';
  if (dataMessage) return dataMessage;

  const amount = notification?.data?.amount || notification?.data?.wallet_credit_amount;
  if (amount && notification?.type === 'payment_received') {
    return `Encaissement client: Montant ${amount}`;
  }

  return 'Nouvelle notification';
}

export const useNotificationsStore = defineStore('notifications', {
  state: () => ({
    notificationCount: 0,
    notifications: [],
    loading: false,
    localFallbackNotifications: readLocalFallbackNotifications(),
  }),

  getters: {
    hasUnreadNotifications: (state) => state.notificationCount > 0,
    recentNotifications: (state) => state.notifications.slice(0, 5)
  },

  actions: {
    persistLocalFallbackNotifications() {
      try {
        localStorage.setItem(LOCAL_NOTIFICATIONS_KEY, JSON.stringify(this.localFallbackNotifications));
      } catch (error) {
        console.warn('Impossible de persister les notifications locales:', error);
      }
    },

    normalizeNotification(notification = {}) {
      return {
        id: notification.id || `local-${Date.now()}-${Math.floor(Math.random() * 10000)}`,
        type: notification.type || 'info',
        message: resolveNotificationMessage(notification),
        data: notification.data || {},
        read_at: notification.read_at || null,
        created_at: notification.created_at || new Date().toISOString(),
        local_only: Boolean(notification.local_only)
      };
    },

    mergeNotifications(serverNotifications = [], params = {}) {
      const normalizedServer = Array.isArray(serverNotifications)
        ? serverNotifications.map((item) => this.normalizeNotification(item))
        : [];

      const normalizedLocal = this.localFallbackNotifications.map((item) => this.normalizeNotification(item));
      const mergedMap = new Map();

      [...normalizedLocal, ...normalizedServer].forEach((item) => {
        mergedMap.set(String(item.id), item);
      });

      let merged = sortByDateDesc([...mergedMap.values()]);

      // Eviter les doublons quand une notification locale optimiste est suivie
      // de la meme notification provenant du serveur (IDs differents).
      const signatureMap = new Map();
      merged.forEach((item) => {
        const signature = buildNotificationSignature(item);
        if (!signature) {
          signatureMap.set(`id:${item.id}`, item);
          return;
        }

        const existing = signatureMap.get(signature);
        if (!existing) {
          signatureMap.set(signature, item);
          return;
        }

        const existingIsLocalOnly = Boolean(existing.local_only);
        const currentIsLocalOnly = Boolean(item.local_only);

        // Preferer la notification serveur quand elle existe.
        if (existingIsLocalOnly && !currentIsLocalOnly) {
          signatureMap.set(signature, item);
        }
      });

      merged = sortByDateDesc([...signatureMap.values()]);

      if (params.unread) {
        merged = merged.filter((item) => !item.read_at);
      }

      if (params.perPage) {
        merged = merged.slice(0, params.perPage);
      }

      this.notifications = merged;
      return merged;
    },

    getLocalUnreadCount() {
      return this.localFallbackNotifications.filter(
        (notification) => notification.local_only && !notification.read_at
      ).length;
    },

    async loadNotificationCount() {
      try {
        const response = await NotificationsService.getUnreadCount();
        const apiCount = response?.data?.count || 0;
        this.notificationCount = apiCount + this.getLocalUnreadCount();
      } catch (error) {
        console.error('Erreur lors du chargement du compteur de notifications:', error);
        this.notificationCount = this.getLocalUnreadCount();
      }
    },

    async loadRecentNotifications(params = {}) {
      this.loading = true;
      try {
        const response = await NotificationsService.getUserNotifications(params);
        const serverNotifications = response?.data?.data || [];
        const mergedNotifications = this.mergeNotifications(serverNotifications, params);

        const payload = {
          ...(response?.data || {}),
          data: mergedNotifications,
          total: Math.max(response?.data?.total || 0, mergedNotifications.length),
          current_page: response?.data?.current_page || params.page || 1,
          per_page: response?.data?.per_page || params.perPage || 15,
          last_page: response?.data?.last_page || 1
        };

        this.loading = false;
        return payload;
      } catch (error) {
        console.error('Erreur lors du chargement des notifications récentes:', error);
        const localOnlyNotifications = this.mergeNotifications([], params);
        this.loading = false;

        return {
          data: localOnlyNotifications,
          total: localOnlyNotifications.length,
          current_page: params.page || 1,
          per_page: params.perPage || 15,
          last_page: 1
        };
      }
    },

    async updateNotificationCount(count = null) {
      if (count !== null) {
        this.notificationCount = count;
      } else {
        // Si aucun nombre n'est fourni, actualiser depuis l'API
        try {
          const response = await NotificationsService.getUnreadCount();
          const apiCount = response?.data?.count || 0;
          this.notificationCount = apiCount + this.getLocalUnreadCount();
        } catch (error) {
          console.error('Erreur lors de la mise à jour du compteur de notifications:', error);
          this.notificationCount = this.getLocalUnreadCount();
        }
      }
    },

    addNotification(notification) {
      const normalizedNotification = this.normalizeNotification(notification);

      const localExists = this.localFallbackNotifications.some(
        (n) => String(n.id) === String(normalizedNotification.id)
      );

      if (!localExists) {
        this.localFallbackNotifications.unshift(normalizedNotification);
        this.localFallbackNotifications = this.localFallbackNotifications.slice(0, 50);
        this.persistLocalFallbackNotifications();
      }

      const exists = this.notifications.some((n) => String(n.id) === String(normalizedNotification.id));
      if (!exists) {
        this.notifications.unshift(normalizedNotification);
      }

      if (this.notifications.length > 20) {
        this.notifications.pop(); // Limiter la taille du tableau
      }

      // Mise a jour locale immediate du badge
      if (!normalizedNotification.read_at) {
        this.notificationCount += 1;
      }
    },

    async markAsRead(notificationOrId) {
      const notificationId = typeof notificationOrId === 'object'
        ? notificationOrId?.id
        : notificationOrId;
      const targetId = String(notificationId);
      const localMatch = this.localFallbackNotifications.find(
        (notification) => String(notification.id) === targetId
      );
      const isLocalOnlyNotification = Boolean(localMatch?.local_only);

      this.notifications = this.notifications.map((notification) => {
        if (String(notification.id) === targetId && !notification.read_at) {
          return { ...notification, read_at: new Date().toISOString() };
        }
        return notification;
      });

      let localUpdated = false;
      this.localFallbackNotifications = this.localFallbackNotifications.map((notification) => {
        if (String(notification.id) === targetId && !notification.read_at) {
          localUpdated = true;
          return { ...notification, read_at: new Date().toISOString() };
        }
        return notification;
      });

      if (localUpdated) {
        this.persistLocalFallbackNotifications();

        if (isLocalOnlyNotification) {
          this.notificationCount = Math.max(0, this.notificationCount - 1);
          return;
        }
      }

      await NotificationsService.markAsRead(notificationId);
      this.notificationCount = Math.max(0, this.notificationCount - 1);
    },

    async markAllAsRead() {
      this.notifications = this.notifications.map((notification) => ({
        ...notification,
        read_at: notification.read_at || new Date().toISOString()
      }));

      this.localFallbackNotifications = this.localFallbackNotifications.map((notification) => ({
        ...notification,
        read_at: notification.read_at || new Date().toISOString()
      }));
      this.persistLocalFallbackNotifications();

      try {
        await NotificationsService.markAllAsRead();
      } catch (error) {
        console.warn('Marquage global API indisponible, notifications locales marquees comme lues.');
      }

      this.notificationCount = 0;
    }
  }
});